import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar, Cog, Fuel } from 'lucide-react'
import PostnummerFilter from '../../components/PostnummerFilter'
import QueryExplanationBox from '../../components/QueryExplanationBox'
import { useVehicleFilter } from '../../contexts/VehicleFilterContext'
import { useMaxDataDate } from '../../hooks/useMaxDataDate'
import { useMinePostnummer } from '../../hooks/useMinePostnummer'
import { usePersistedState } from '../../hooks/usePersistedState'
import AkseldriftAlder from './AkseldriftAlder'
import AkseldriftOversikt from './AkseldriftOversikt'
import AkseldriftTable from './AkseldriftTable'
import DrivstoffAlder from './DrivstoffAlder'
import DrivstoffHybrid from './DrivstoffHybrid'
import DrivstoffOversikt from './DrivstoffOversikt'
import DrivstoffTable from './DrivstoffTable'
import type { AkseldriftQuery, Drive, FuelCategory, Periods, SegmentRow, SubTab, TopTab } from './types'

type OutputMode = 'year_to_date' | 'month_to_date' | 'last_month' | 'last_week' | 'last_year' | 'custom'

// Labels are translated at render: t(`outputModes.${mode}`)
const OUTPUT_MODES: OutputMode[] = ['year_to_date', 'month_to_date', 'last_month', 'last_week', 'last_year', 'custom']

const TOP_TABS: { key: TopTab; icon: typeof Cog }[] = [
  { key: 'akseldrift', icon: Cog },
  { key: 'drivstoff', icon: Fuel },
]

const SUB_TABS: Record<TopTab, SubTab[]> = {
  akseldrift: ['oversikt', 'merke', 'modell', 'segment', 'alder'],
  drivstoff: ['oversikt', 'merke', 'modell', 'hybrid', 'alder'],
}

const tabClass = (active: boolean) => `px-4 py-2 font-medium border-b-2 transition-colors ${
  active
    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
}`

/**
 * Teknisk (#872, #875): analyses of technical parameters, one first-level tab
 * each, Akseldrift and Drivstoff. Same layout as Alder: header, the filter
 * card for the whole page, then the tabs.
 */
export default function Teknisk() {
  const { t } = useTranslation('teknisk')
  const { vehicleFilter, getVarebilParam } = useVehicleFilter()
  const maxDataDate = useMaxDataDate()

  // Period keys are shared with the other analytics pages, like on Alder
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [customStartDate, setCustomStartDate] = usePersistedState('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState('bp:filter:customEndDate', '')
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('teknisk:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)

  const isCustom = outputMode === 'custom'
  const periodError = !isCustom
    ? null
    : !customStartDate
      ? t('chooseStartDate')
      : !customEndDate
        ? t('chooseEndDate')
        : customStartDate > customEndDate
          ? t('invalidRange')
          : null

  // The query as a primitive string, so the fetch effect re-fires only when it changes
  const draftKey = useMemo(() => {
    const query: AkseldriftQuery = { output_mode: outputMode }
    const varebil = getVarebilParam()
    if (varebil !== undefined) query.varebil = varebil
    if (minePostnummer) query.mine_postnummer = minePostnummer
    if (outputMode === 'custom') {
      query.custom_start_date = customStartDate
      query.custom_end_date = customEndDate
    }
    return JSON.stringify(query)
    // getVarebilParam is derived from vehicleFilter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputMode, vehicleFilter, minePostnummer, customStartDate, customEndDate])

  const [appliedKey, setAppliedKey] = useState<string | null>(() => (periodError ? null : draftKey))

  // A preset period, the vehicle type and the postnummer filter apply at once;
  // a custom period waits for "Oppdater", as on Alder.
  useEffect(() => {
    if (!isCustom) setAppliedKey(draftKey)
  }, [isCustom, draftKey])

  const handleApply = () => {
    if (!periodError) setAppliedKey(draftKey)
  }

  // Not persisted: the page always opens on Akseldrift › Oversikt (AC1)
  const [topTab, setTopTab] = useState<TopTab>('akseldrift')
  const [subTab, setSubTab] = useState<SubTab>('oversikt')
  const [sortDrive, setSortDrive] = useState<Drive | null>(null)
  const [sortKategori, setSortKategori] = useState<FuelCategory | null>(null)
  // Drivstoff's «Ekskluder elbil»: off on every visit, applies to every sub-tab but Hybrid (#875 AC5)
  const [ekskluderElbil, setEkskluderElbil] = useState(false)
  // Akseldrift › Segment drilldown (#911): kept across filter changes, cleared on a tab click
  const [segment, setSegment] = useState<SegmentRow | null>(null)

  const openTab = (top: TopTab, sub: SubTab) => {
    setTopTab(top)
    setSubTab(sub)
    setSortDrive(null)
    setSortKategori(null)
    setSegment(null)
  }

  // «Se alle» under a top-10 table opens Merke or Modell sorted on that akseldrift
  const handleSeeAll = (tab: 'merke' | 'modell', drive: Drive) => {
    setSubTab(tab)
    setSortDrive(drive)
  }

  // … and on Drivstoff, with that category picked and sorted on its count (#875 AC8)
  const handleSeeAllKategori = (tab: 'merke' | 'modell', kategori: FuelCategory) => {
    setSubTab(tab)
    setSortKategori(kategori)
  }

  const switchDisabled = subTab === 'hybrid'
  const switchOn = ekskluderElbil && !switchDisabled

  // The explanation box describes the query the shown answer came from: the
  // applied filters, the periods the backend resolved, and what is counted.
  const [periods, setPeriods] = useState<Periods | null>(null)
  useEffect(() => { setPeriods(null) }, [appliedKey])
  const applied: AkseldriftQuery | null = appliedKey ? JSON.parse(appliedKey) : null
  const explanationNotes = [
    topTab === 'akseldrift' ? t('explanation.akseldrift') : t('explanation.drivstoff'),
    ...(topTab === 'drivstoff' && switchOn ? [t('explanation.elbilExcluded')] : []),
    ...(subTab === 'alder' ? [t('explanation.alder')] : []),
  ]

  const clampToMax = (value: string) => (maxDataDate && value > maxDataDate ? maxDataDate : value)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">{t('subtitle')}</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('source')}</p>
      </div>

      <div className="card p-4" data-testid="teknisk-filter-card">
        <div className="flex flex-wrap items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('period')}</span>
          <div className="flex flex-wrap gap-2">
            {OUTPUT_MODES.map(mode => (
              <button
                key={mode}
                onClick={() => setOutputMode(mode)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  outputMode === mode
                    ? 'bg-primary-600 text-white shadow-xs'
                    : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border'
                }`}
              >
                {t(`outputModes.${mode}` as const)}
              </button>
            ))}
          </div>
          {isCustom && (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="teknisk-start" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('startDate')}
                </label>
                <input
                  id="teknisk-start"
                  type="date"
                  value={customStartDate}
                  onChange={e => setCustomStartDate(clampToMax(e.target.value))}
                  max={maxDataDate || undefined}
                  className="input-field"
                />
              </div>
              <div>
                <label htmlFor="teknisk-end" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('endDate')}
                </label>
                <input
                  id="teknisk-end"
                  type="date"
                  value={customEndDate}
                  onChange={e => setCustomEndDate(clampToMax(e.target.value))}
                  max={maxDataDate || undefined}
                  className="input-field"
                />
              </div>
              {periodError && (
                <p role="alert" className="text-sm font-medium text-red-600 dark:text-red-400 pb-2">
                  {periodError}
                </p>
              )}
            </div>
          )}
          <div className="ml-auto">
            <button
              onClick={handleApply}
              className={`btn-primary text-sm ${periodError ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!!periodError}
            >
              {t('apply')}
            </button>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        </div>
      </div>

      <div className="flex gap-2 border-b border-gray-200 dark:border-dark-border" role="tablist" data-testid="teknisk-top-tabs">
        {TOP_TABS.map(({ key, icon: Icon }) => (
          <button key={key} role="tab" aria-selected={topTab === key} onClick={() => openTab(key, 'oversikt')} className={tabClass(topTab === key)}>
            <span className="flex items-center gap-2">
              <Icon className="w-4 h-4" />
              {t(`tabs.${key}` as const)}
            </span>
          </button>
        ))}
      </div>

      {/* Same underline tabs as Alder's Merke / Modell */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 dark:border-dark-border">
        <div className="flex gap-2" role="tablist" data-testid="teknisk-sub-tabs">
          {SUB_TABS[topTab].map(tab => (
            <button key={tab} role="tab" aria-selected={subTab === tab} onClick={() => openTab(topTab, tab)} className={tabClass(subTab === tab)}>
              {t(`subTabs.${tab}` as const)}
            </button>
          ))}
        </div>
        {topTab === 'drivstoff' && (
          <div
            className={`flex items-center gap-2 pb-1 text-sm font-medium ${
              switchDisabled ? 'text-gray-400 dark:text-gray-600' : 'text-gray-700 dark:text-gray-300'
            }`}
            title={switchDisabled ? t('drivstoff.excludeElbilNotHere') : undefined}
          >
            <button
              type="button"
              role="switch"
              id="drivstoff-ekskluder-elbil"
              aria-checked={switchOn}
              disabled={switchDisabled}
              onClick={() => setEkskluderElbil(v => !v)}
              className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                switchOn ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${switchOn ? 'translate-x-4' : ''}`} />
            </button>
            <label htmlFor="drivstoff-ekskluder-elbil" className={switchDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}>
              {t('drivstoff.excludeElbil')}
            </label>
          </div>
        )}
      </div>

      {applied && periods && (
        <div data-testid="teknisk-query-explanation">
          <QueryExplanationBox
            periods={periods}
            maxDataDate={maxDataDate}
            outputMode={applied.output_mode}
            vehicleFilter={applied.varebil === true ? 'varebil' : applied.varebil === false ? 'personbil' : 'alle'}
            postnummerFilter={applied.mine_postnummer ?? null}
            extraWarnings={explanationNotes}
          />
        </div>
      )}

      {!appliedKey ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('noFetchUntilValid')}</p>
      ) : topTab === 'drivstoff' ? (
        subTab === 'oversikt' ? (
          <DrivstoffOversikt queryKey={appliedKey} ekskluderElbil={ekskluderElbil} onSeeAll={handleSeeAllKategori} onPeriods={setPeriods} />
        ) : subTab === 'hybrid' ? (
          <DrivstoffHybrid queryKey={appliedKey} onPeriods={setPeriods} />
        ) : subTab === 'alder' ? (
          <DrivstoffAlder queryKey={appliedKey} ekskluderElbil={ekskluderElbil} onPeriods={setPeriods} />
        ) : (
          // Remount on «Se alle» so the table starts on that category
          <DrivstoffTable key={`${subTab}-${sortKategori}`} kind={subTab === 'modell' ? 'modell' : 'merke'} queryKey={appliedKey}
            ekskluderElbil={ekskluderElbil} sortKategori={sortKategori} onPeriods={setPeriods} />
        )
      ) : subTab === 'oversikt' ? (
        <AkseldriftOversikt queryKey={appliedKey} onSeeAll={handleSeeAll} onPeriods={setPeriods} />
      ) : subTab === 'alder' ? (
        <AkseldriftAlder queryKey={appliedKey} onPeriods={setPeriods} />
      ) : subTab === 'segment' ? (
        segment ? (
          <AkseldriftTable key={`segment-${segment.segment_id}`} kind="modell" queryKey={appliedKey} sortDrive={null}
            segment={segment} onBack={() => setSegment(null)} onPeriods={setPeriods} />
        ) : (
          <AkseldriftTable key="segment" kind="segment" queryKey={appliedKey} sortDrive={null}
            onSelectSegment={setSegment} onPeriods={setPeriods} />
        )
      ) : (
        // Remount on «Se alle» so the table starts sorted on that akseldrift
        <AkseldriftTable key={`${subTab}-${sortDrive}`} kind={subTab === 'modell' ? 'modell' : 'merke'} queryKey={appliedKey} sortDrive={sortDrive} onPeriods={setPeriods} />
      )}
    </div>
  )
}
